import {IncomingHttpHeaders} from 'http';
import lruCache from 'lru-cache';
import {Task} from 'redux-saga';
import {
  put,
  delay,
  fork,
  cancel,
  select,
  call,
  CallEffect,
} from 'redux-saga/effects';
import callApi, {I_ApiResult, IParamApi, callApiList} from './api';
import {ApiResultCode} from './api/apiError';
import {getFetchKey} from './fetch/fetchUtil';
import {ReduxAction} from './redux';
import {actions as commonSubActions} from '../../commonSub/state/action';
import {IS_CLIENT} from '../constant';
import {getIsGeneratorFunction} from '../function';
import useTypedSelector from '../hook/useTypedSelector';
import {actions, ISetFetchStatusParam} from '../state/action';
import {IReduxState} from '../store';

function makeCheckSlowSaga(actionType: string, fetchKey: string) {
  return function* () {
    yield delay(500);
    yield put(
      actions.setIsSlow({
        actionType,
        fetchKey,
        isSlow: true,
      })
    );
  };
}

// TODO: 메인홈은 주로 SSR로 첫 데이터를 가져오므로, 리덕스에서 캐싱하자
const apiCache = new lruCache<string, I_ApiResult<any>>({
  max: 500,
  /**
   * 정적 파일 생성 주기가 2분이므로 서버 API 캐싱은 그보다 작은 1분으로 설정한다
   * 그래야 정적 파일 생성 시 항상 업데이트 된다는 것이 보장된다
   */
  maxAge: IS_CLIENT ? 1000 * 60 * 2 : 1000 * 60 * 1,
});

const SAGA_CALL_TYPE = call(() => {}).type;
function getIsCallEffect(value: any): value is CallEffect {
  return value && value.type === SAGA_CALL_TYPE;
}
export interface IPagingParam {
  cursor?: string;
  offset?: number;
  limit: number;
}
export const API_DEFAULT_LIMIT = 30;
export function makeFetchSaga({
  fetchSaga,
  canCache,
  isIgnoreError,
  getTotalCount = res => res?.meta?.pagination?.totalCount,
}: {
  fetchSaga: any;
  canCache: boolean;
  isIgnoreError?: boolean;
  getTotalCount?: (res: I_ApiResult<any>) => any;
}) {
  return function* (action: ReduxAction) {
    const {type: actionType, isFirst, fetchOffset} = action;
    const fetchKey = getFetchKey(action);
    const [nextPaging, lang, headers]: [
      string | number | undefined,
      string,
      IncomingHttpHeaders | undefined,
    ] = yield select((state: IReduxState) => [
      state.common.fetchInfo.nextPagingMap[actionType]?.[fetchKey],
      state.common.lang,
      state.common.headers,
    ]);

    const pagingParam: IPagingParam = {
      cursor:
        !isFirst && typeof nextPaging === 'string' ? nextPaging : undefined,
      offset: isFirst
        ? 0
        : fetchOffset ?? (typeof nextPaging === 'number' ? nextPaging : 0),
      limit: API_DEFAULT_LIMIT,
    };
    const isFirstFetch = !pagingParam.cursor && !pagingParam.offset;

    const iterStack: Array<IterableIterator<any>> = [];
    let iter: IterableIterator<any> = fetchSaga(action, pagingParam);
    let res;
    let checkSlowTask: Task | undefined;
    let params: ISetFetchStatusParam | undefined;
    let isUnexpectedError = false;
    let error: Error | undefined;
    while (true) {
      let value: any;
      let done: boolean | undefined;
      try {
        ({value, done} = iter.next(res));
      } catch (e: any) {
        done = true;
        isUnexpectedError = true;
        error = e;
      }
      if (getIsCallEffect(value) && getIsGeneratorFunction(value.payload.fn)) {
        iterStack.push(iter);
        iter = value.payload.fn(...value.payload.args);
        continue;
      }
      if (
        getIsCallEffect(value) &&
        (value.payload.fn === callApi || value.payload.fn === callApiList)
      ) {
        yield put(
          actions.setFetchStatus({
            actionType,
            fetchKey,
            status: 'request',
            isFirstFetch,
          })
        );
        const apiParam: IParamApi | IParamApi[] = value.payload.args[0];
        let apiResult: I_ApiResult<any> | undefined;
        let cacheKey: string | undefined;
        if (Array.isArray(apiParam)) {
          apiParam.forEach(param => {
            param.lang = lang;
            param.headers ??= {...headers};
          });
        } else {
          apiParam.lang = lang;
          apiParam.headers ??= {...headers};
          const isUseCache = action.isUseCache ?? canCache;
          cacheKey = getApiCacheKey(actionType, apiParam);
          apiResult =
            isUseCache && apiCache.has(cacheKey)
              ? apiCache.get(cacheKey)
              : undefined;
        }

        const isFromCache = !!apiResult;
        if (!isFromCache) {
          checkSlowTask = yield fork(makeCheckSlowSaga(actionType, fetchKey));
          apiResult = yield value;
          if (checkSlowTask) {
            yield cancel(checkSlowTask);
          }
        }
        res = apiResult;
        if (apiResult) {
          const isSuccess = apiResult.success;
          if (isSuccess && canCache && cacheKey && !isFromCache) {
            apiCache.set(cacheKey, apiResult);
          }
          const pagination = apiResult.meta?.pagination;
          let nextPaging: string | number | undefined;
          if (pagination?.cursor) {
            nextPaging = pagination.cursor;
          } else if (
            pagination?.offset !== undefined &&
            pagination?.limit !== undefined
          ) {
            nextPaging = pagination.offset + pagination.limit;
          }
          const isEnd = pagination?.last;
          isUnexpectedError =
            isUnexpectedError ||
            apiResult.resultCode === ApiResultCode.UnexpectedError;
          if (apiResult.resultCode === ApiResultCode.UnexpectedError) {
            error = apiResult.error as Error;
          }
          params =
            isUnexpectedError && params
              ? params
              : {
                  actionType,
                  fetchKey,
                  status: isSuccess ? 'success' : 'fail',
                  totalCount: getTotalCount(apiResult),
                  nextPaging,
                  isEnd,
                  isFirstFetch,
                  resultCode: apiResult.resultCode,
                  resultMessage: isUnexpectedError
                    ? String(apiResult.error)
                    : apiResult.resultMessage,
                };
          if (apiResult.resultCode === ApiResultCode.ServiceMaintenance) {
            yield put(
              commonSubActions.setValue(
                'maintenancePopup',
                value.payload.fn === callApiList
                  ? apiResult.data[0]
                  : apiResult.data
              )
            );
          } else if (apiResult.alert) {
            yield put(actions.addAlert(apiResult.alert));
          }
        }
      } else if (value !== undefined) {
        // @ts-ignore 타입 지정
        res = yield value;
      }
      if (done) {
        const nextIter = iterStack.pop();
        if (nextIter) {
          iter = nextIter;
          continue;
        }

        if (params) {
          yield put(actions.setFetchStatus(params));
        }
        if (isUnexpectedError && !isIgnoreError) {
          if (!IS_CLIENT) {
            // 이렇게 예외를 발생시켜야 SSR 시 /_error 페이지가 렌더링된다
            throw (
              error ??
              new Error(params?.resultMessage || 'unexpected api error')
            );
          } else {
            // TODO: API 에러 메시지 보여주기
            const isRetry = false;
            if (isRetry) {
              isUnexpectedError = false;
              iter = fetchSaga(action, pagingParam);
            } else {
              break;
            }
          }
        } else {
          break;
        }
      }
    }
  };
}

export function useFetchInfo(actionType: string, fetchKey?: string | number) {
  const _fetchKey = getFetchKey({type: actionType, fetchKey});
  return useTypedSelector(state => {
    const fetchStatus =
      state.common.fetchInfo.fetchStatusMap[actionType]?.[_fetchKey];
    const isFetching =
      state.common.fetchInfo.fetchStatusMap[actionType]?.[_fetchKey] ===
      'request';
    return {
      fetchStatus,
      isLoading: fetchStatus === undefined || isFetching,
      isFetching,
      isFetched:
        state.common.fetchInfo.fetchStatusMap[actionType]?.[_fetchKey] ===
          'success' ||
        state.common.fetchInfo.fetchStatusMap[actionType]?.[_fetchKey] ===
          'fail',
      isSlow: !!state.common.fetchInfo.isSlowMap[actionType]?.[_fetchKey],
      isFirstFetched:
        !!state.common.fetchInfo.isFirstFetchedMap[actionType]?.[_fetchKey],
      isEnd: state.common.fetchInfo.isEndMap[actionType]?.[_fetchKey] ?? false,
      totalCount:
        state.common.fetchInfo.totalCountMap[actionType]?.[_fetchKey] ?? 0,
      errorCode: state.common.fetchInfo.errorCodeMap[actionType]?.[_fetchKey],
      errorMessage:
        state.common.fetchInfo.errorMessageMap[actionType]?.[_fetchKey],
    };
  });
}

// 쿼리 파라미터 순서가 바뀌어도 같은 key가 나오도록 키 이름으로 정렬한다
export function getApiCacheKey(
  actionType: string,
  {apiHost, url, params}: IParamApi
) {
  const prefix = `${actionType}_${apiHost ? apiHost + url : url}`;
  params =
    params instanceof URLSearchParams
      ? [...params].reduce<{[key: string]: string}>(
          (acc, item) => ((acc[item[0]] = item[1]), acc),
          {}
        )
      : params;
  const keys = params ? Object.keys(params) : [];

  if (keys.length) {
    return (
      prefix +
      keys
        .sort()
        .reduce((acc, key) => `${acc}&${key}=${(params as any)[key]}`, '') // key는 params로 부터 가져온 것이므로 any를 써도 괜찮다
    );
  } else {
    return prefix;
  }
}
